import { useEffect, useRef, useState } from 'react';
import type { User } from '@jungles/shared';
import { api, ApiError, getSessionEpoch } from '../lib/api';
import { useSession } from '../app/session';
import { useToast } from '../app/toast';
import { TelegramAvatar } from './TelegramAvatar';
import { Dialog } from './Sheet';
import './profile-photo-editor.css';
type Photo={mediaType:string;imageBase64:string};
// Survive same-owner route reentry; never admit competing uploads.
const uploads=new Set<string>(),uploadListeners=new Set<()=>void>();
const notifyUploads=()=>uploadListeners.forEach(fn=>fn());
export function ProfilePhotoEditor({user,name}:{user:User;name:string}) {
  const input=useRef<HTMLInputElement>(null),trigger=useRef<HTMLButtonElement>(null);
  const alive=useRef(true),lock=useRef(false),epoch=useRef(getSessionEpoch());
  const ownerKey=epoch.current+':'+user.id;
  const [enabled,setEnabled]=useState(false),[busy,setBusy]=useState(()=>uploads.has(ownerKey)),[draft,setDraft]=useState<Photo|null>(null),[error,setError]=useState('');
  const {updateIdentity}=useSession();const toast=useToast();
  const active=()=>epoch.current===getSessionEpoch();
  useEffect(()=>{const changed=()=>setBusy(uploads.has(ownerKey));uploadListeners.add(changed);changed();return()=>{uploadListeners.delete(changed)}},[ownerKey]);
  useEffect(()=>{alive.current=true;void api.get<{enabled:boolean}>('/api/me/photo/status').then(r=>{if(alive.current&&active())setEnabled(r.enabled)}).catch(()=>{});return()=>{alive.current=false}},[]);
  useEffect(()=>{if(error)return()=>{queueMicrotask(()=>{if(alive.current)trigger.current?.focus({preventScroll:true})})}},[error]);
  const upload=async(photo:Photo)=>{
    if(!active())return;
    const next=await api.post<{photoUrl:string}>('/api/me/photo',photo);
    if(!active())return;
    // Apply only the committed field, never a stale full-user readback.
    updateIdentity(user.id,{photoUrl:next.photoUrl});
    if(alive.current){setDraft(null);setError('')}
    toast('Фото сохранено');
  };
  const attempt=async(file?:File)=>{
    if(lock.current||uploads.has(ownerKey)||!active()||(!file&&!draft))return;
    lock.current=true;uploads.add(ownerKey);notifyUploads();setBusy(true);setError('');
    try {
      let photo=draft;
      if(file){
        setDraft(null);
        if(!file.size||file.size>5*1024*1024)throw Error('Выберите фото до 5 МБ');
        const url=await new Promise<string>((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(String(reader.result));reader.onerror=()=>reject(Error('Не удалось прочитать фото. Выберите другое'));reader.readAsDataURL(file)});
        if(!active())return;
        const mediaType=['image/jpeg','image/png','image/webp','image/heic','image/heif'].includes(file.type)?file.type:'application/octet-stream';
        photo={mediaType,imageBase64:url.slice(url.indexOf(',')+1)};
        if(alive.current)setDraft(photo);
      }
      if(photo)await upload(photo);
    }catch(e){
      if(active()){
        const message=e instanceof ApiError ? (e.status===413?'Фото слишком большое. Выберите фото до 5 МБ':e.message) : e instanceof SyntaxError?'Сервер не принял фото. Попробуйте фото меньшего размера':e instanceof Error?e.message:'Не удалось сохранить фото. Повторите';
        if(alive.current)setError(message);else toast('Фото не сохранено. Откройте настройки и выберите фото снова');
      }
    }finally{lock.current=false;uploads.delete(ownerKey);notifyUploads();if(alive.current&&active())setBusy(false)}
  };
  return <div className="profile-photo-editor">
    <TelegramAvatar photoUrl={user.photoUrl} name={name}/>
    {enabled&&<><input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" hidden onChange={e=>{const file=e.target.files?.[0];e.target.value='';if(file)void attempt(file)}}/>
    <button ref={trigger} className="profile-photo-pencil" type="button" aria-label="Изменить фото" disabled={busy} onClick={()=>input.current?.click()}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m16 3 5 5-12 12-6 1 1-6Z M14 5l5 5"/></svg>
    </button></>}
    {busy&&<span className="profile-photo-reading" role="status">Сохраняем фото…</span>}
    {error&&<Dialog title="Фото не сохранено" confirmLabel={draft?'Повторить':'Выбрать другое фото'} onConfirm={()=>{if(draft)void attempt();else{setError('');input.current?.click()}}} onCancel={()=>{setDraft(null);setError('')}}>
      <p role="alert">{error}</p><p className="nt">Прежнее фото не изменено. JPEG, PNG, WebP или HEIC · до 5 МБ и 16 Мп. Выбранное фото сохраняется автоматически и видно другим участникам.</p>
    </Dialog>}
  </div>;
}
